import apiClient from '../lib/axios';

const mockFallbackEnabled = (() => {
  const raw = String(
    process.env.REACT_APP_ADMIN_MOCK_FALLBACK
      || process.env.VITE_ADMIN_MOCK_FALLBACK
      || ''
  ).trim().toLowerCase();
  return raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on';
})();

const shouldFallback = (error) => {
  const status = error?.response?.status;
  if (!status) return true;
  return status === 404 || status >= 500;
};

async function requestStrict(requestFn) {
  const response = await requestFn();
  return response.data;
}

async function requestWithFallback(requestFn, fallbackData) {
  try {
    const response = await requestFn();
    return response.data;
  } catch (error) {
    if (mockFallbackEnabled && shouldFallback(error)) {
      return fallbackData;
    }
    throw error;
  }
}

const mockSummary = {
  period: 'day',
  gmv: 12450,
  admin_revenue: 1245,
  orders: 128,
  avg_ticket: 97.2,
  stores_open: 12,
  stores_closed: 4,
  couriers_online: 9,
  ticket_series: [
    { label: 'Seg', value: 84 },
    { label: 'Ter', value: 92 },
    { label: 'Qua', value: 110 },
    { label: 'Qui', value: 97 },
    { label: 'Sex', value: 115 },
    { label: 'Sab', value: 140 },
    { label: 'Dom', value: 130 }
  ],
  orders_series: [
    { label: 'Seg', value: 18 },
    { label: 'Ter', value: 20 },
    { label: 'Qua', value: 24 },
    { label: 'Qui', value: 22 },
    { label: 'Sex', value: 28 },
    { label: 'Sab', value: 32 },
    { label: 'Dom', value: 26 }
  ],
  top_stores_gmv: [
    { id: '1', name: 'Loja 1', value: 4200 },
    { id: '2', name: 'Loja 2', value: 3100 }
  ],
  top_stores_orders: [
    { id: '3', name: 'Loja 3', value: 38 },
    { id: '4', name: 'Loja 4', value: 31 }
  ],
  open_stores: [
    { id: '1', name: 'Loja 1', city: 'Rosana' },
    { id: '2', name: 'Loja 2', city: 'Primavera' }
  ],
  closed_stores: [
    { id: '5', name: 'Loja 5', city: 'Teodoro' }
  ],
  online_couriers: [
    { id: '1', name: 'Motoboy 1', lat: -22.5, lng: -52.95 }
  ],
  fee_percent: 10
};

const mockStores = [
  {
    id: '1',
    name: 'Loja 1',
    email: 'loja1@teste.com',
    category: 'Restaurante',
    status: 'active',
    is_open: true,
    gmv_day: 1200,
    gmv_month: 6000,
    gmv_year: 28000,
    orders_day: 18,
    avg_ticket: 65,
    fee_percent: 10
  }
];

const mockStoreOrders = {
  store_id: '1',
  store_name: 'Loja 1',
  fee_percent: 10,
  summary: {
    all: 2,
    pending: 0,
    completed: 2,
    cancelled: 0,
    by_payment: { cash: 0, pix: 1, card: 0, voucher: 1 },
    completed_platform: 0,
    completed_own: 2,
    store_payout_pending: 126,
    store_payout_paid: 0,
    cash_future_fee_pending: 0
  },
  orders: [
    {
      id: 'order-1',
      order_number: 'ORD-100001',
      status: 'delivered',
      status_group: 'completed',
      payment_method: 'pix',
      payment_status: 'paid',
      subtotal: 85,
      delivery_fee: 0,
      discount: 0,
      total: 85,
      admin_fee: 8.5,
      delivery_assignment_type: 'own',
      delivery_assignment_label: 'Motoboy proprio',
      courier_id: null,
      courier_name: null,
      cash_debt_status: null,
      cash_debt_due_date: null,
      store_payout: {
        status: 'pending',
        amount: 76.5,
        paid_at: null,
        paid_by: null,
        note: null,
      },
      created_at: new Date().toISOString(),
      items: [
        { id: 'item-1', product_id: 'prod-1', product_name: 'Burger', quantity: 2, unit_price: 30, subtotal: 60, extras_total: 0 }
      ]
    },
    {
      id: 'order-2',
      order_number: 'ORD-100002',
      status: 'delivered',
      status_group: 'completed',
      payment_method: 'voucher',
      payment_status: 'approved',
      voucher_issuer: 'ticket',
      voucher_type: 'alimentacao',
      subtotal: 55,
      delivery_fee: 0,
      discount: 0,
      total: 55,
      admin_fee: 5.5,
      delivery_assignment_type: 'own',
      delivery_assignment_label: 'Motoboy proprio',
      courier_id: null,
      courier_name: null,
      cash_debt_status: null,
      cash_debt_due_date: null,
      store_payout: {
        status: 'pending',
        amount: 49.5,
        paid_at: null,
        paid_by: null,
        note: null,
      },
      created_at: new Date().toISOString(),
      items: [
        { id: 'item-2', product_id: 'prod-2', product_name: 'Marmita', quantity: 1, unit_price: 55, subtotal: 55, extras_total: 0 }
      ]
    }
  ]
};

const mockStoreProducts = {
  store_id: '1',
  store_name: 'Loja 1',
  summary: { total: 2, available: 2, unavailable: 0 },
  products: [
    {
      id: 'prod-1',
      name: 'Burger',
      description: 'Artesanal',
      price: 30,
      available: true,
      image_url: '',
      extras_count: 2,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }
  ]
};

const mockStoreFinance = {
  store_id: '1',
  store_name: 'Loja 1',
  payout_schedule: 'weekly',
  fee_percent_override: null,
  fee_percent_effective: 10,
  totals: {
    orders_count: 2,
    gross_revenue: 140,
    admin_fee: 14,
    net_repass: 126,
    completed_orders_count: 2,
    completed_platform_orders_count: 0,
    completed_own_orders_count: 2,
    delivery_fee_own: 0,
    delivery_fee_platform_excluded: 0,
    store_payout_pending: 126,
    store_payout_paid: 0,
    store_payout_pending_orders: 2,
    store_payout_paid_orders: 0,
    cash_orders_count: 0,
    cash_debt_pending: 0,
    cash_debt_scheduled: 0,
    cash_debt_collected: 0,
    cash_debt_cancelled: 0
  },
  orders: mockStoreOrders.orders,
  voucher_commissions: {
    summary: {
      count: 1,
      gross_amount: 55,
      commission_amount: 5.5,
      merchant_net_amount: 49.5,
      by_status: {
        pending: { count: 1, commission_amount: 5.5 },
        invoiced: { count: 0, commission_amount: 0 },
        paid: { count: 0, commission_amount: 0 },
        overdue: { count: 0, commission_amount: 0 },
      },
    },
  },
};

const mockStoreVoucherCommissions = {
  store_id: '1',
  store_name: 'Loja 1',
  filters: {
    status: 'all',
    limit: 500,
  },
  summary: {
    count: 1,
    gross_amount: 55,
    commission_amount: 5.5,
    merchant_net_amount: 49.5,
    by_status: {
      pending: { count: 1, commission_amount: 5.5 },
      invoiced: { count: 0, commission_amount: 0 },
      paid: { count: 0, commission_amount: 0 },
      overdue: { count: 0, commission_amount: 0 },
    },
  },
  commissions: [
    {
      id: 'commission-1',
      order_id: 'order-2',
      restaurant_id: '1',
      status: 'pending',
      gross_amount: 55,
      commission_amount: 5.5,
      merchant_net_amount: 49.5,
      fee_percent: 10,
      due_date: null,
      invoiced_at: null,
      paid_at: null,
      note: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
  ],
};

const mockStorePayouts = {
  summary: {
    stores_total: 1,
    stores_with_pending: 1,
    completed_orders_count: 1,
    completed_platform_orders_count: 0,
    completed_own_orders_count: 1,
    store_payout_pending: 76.5,
    store_payout_paid: 0,
    delivery_fee_platform_excluded: 0,
    delivery_fee_own: 0,
  },
  stores: [
    {
      store_id: '1',
      store_name: 'Loja 1',
      store_email: 'loja1@teste.com',
      status: 'pending',
      payout_schedule: 'weekly',
      totals: {
        completed_orders_count: 1,
        completed_platform_orders_count: 0,
        completed_own_orders_count: 1,
        gross_revenue: 85,
        admin_fee: 8.5,
        net_repass: 76.5,
        delivery_fee_platform_excluded: 0,
        delivery_fee_own: 0,
        store_payout_pending: 76.5,
        store_payout_paid: 0,
        store_payout_pending_orders: 1,
        store_payout_paid_orders: 0,
      },
      resulmetodos_path: '/admin/lojas/1?tab=Pedidos&status=completed',
      finance_path: '/admin/lojas/1?tab=Financeiro',
    },
  ],
};

const mockStoreAudit = {
  store_id: '1',
  store_name: 'Loja 1',
  logs: [
    {
      id: 'audit-1',
      actor_email: 'admin@teste.com',
      action: 'store_admin_settings_updated',
      entity_type: 'store',
      entity_id: '1',
      details: { store_id: '1', fee_percent_override: 10 },
      created_at: new Date().toISOString()
    }
  ]
};

const mockStoreSettings = {
  store_id: '1',
  store_name: 'Loja 1',
  global_fee_percent: 10,
  fee_percent_override: null,
  fee_percent_effective: 10,
  payout_schedule: 'weekly',
  voucher: {
    accepts_voucher: true,
    accepted_issuers: ['Ticket', 'Pluxee'],
    accepted_types: ['alimentacao', 'refeicao'],
    partner_store_id: 'mock-partner-1',
    approval_status: 'approved',
    enabled_for_checkout: true,
  },
  cash_future_fee_pending: 8.5,
  cash_future_fee_cancelled: 0,
  cash_future_fee_collected: 0,
  voucher_commissions: {
    summary: mockStoreVoucherCommissions.summary,
  },
};

const mockCouriers = [
  { id: '1', name: 'Motoboy 1', status: 'active', is_online: true, lat: -22.5, lng: -52.95 }
];

const mockCourierOrders = {
  courier: {
    id: '1',
    name: 'Motoboy 1',
    email: 'motoboy1@teste.com',
    payout_method: 'pix',
    payout_schedule: 'weekly',
    payout_schedule_requested: null
  },
  summary: {
    total_orders: 2,
    waiting_orders: 1,
    finished_orders: 1,
    cancelled_orders: 0,
    unpaid_orders: 1,
    paid_orders: 0,
    total_delivery_fee_cents: 2200,
    total_courier_value_cents: 1900,
    waiting_courier_value_cents: 900,
    finished_courier_value_cents: 1000,
    unpaid_courier_value_cents: 1000,
    paid_courier_value_cents: 0,
    ready_for_payout_cents: 1000,
    waiting_next_cycle_cents: 0,
    next_payout_due_at: new Date().toISOString()
  },
  items: [
    {
      id: 'order-1',
      order_number: 'ORD-100001',
      status: 'delivered',
      status_group: 'finished',
      restaurant_id: 'store-1',
      restaurant_name: 'Loja 1',
      payment_method: 'pix',
      payment_status: 'paid',
      delivery_fee_cents: 1200,
      admin_fee_cents: 200,
      courier_value_cents: 1000,
      courier_value_confirmed: true,
      payout_status: 'pending',
      payout_schedule: 'weekly',
      payout_due_at: new Date().toISOString(),
      payout_paid_at: null,
      created_at: new Date().toISOString(),
      delivered_at: new Date().toISOString(),
      cancelled_at: null,
      status_updated_at: new Date().toISOString()
    },
    {
      id: 'order-2',
      order_number: 'ORD-100002',
      status: 'out_for_delivery',
      status_group: 'waiting',
      restaurant_id: 'store-1',
      restaurant_name: 'Loja 1',
      payment_method: 'cash',
      payment_status: 'pending',
      delivery_fee_cents: 1000,
      admin_fee_cents: 100,
      courier_value_cents: 900,
      courier_value_confirmed: false,
      payout_status: 'pending',
      payout_schedule: 'weekly',
      payout_due_at: null,
      payout_paid_at: null,
      created_at: new Date().toISOString(),
      delivered_at: null,
      cancelled_at: null,
      status_updated_at: new Date().toISOString()
    }
  ]
};

const mockCourierVerifications = [
  {
    id: '1',
    name: 'Motoboy 1',
    email: 'motoboy1@teste.com',
    phone: '44999999999',
    status: 'active',
    verification: {
      status: 'pending',
      note: null,
      submitted_at: new Date().toISOString(),
      reviewed_at: null,
      reviewed_by: null,
      face_image_url: '',
      document_image_url: '',
      has_face_image: false,
      has_document_image: false
    }
  }
];

const mockCustomers = [
  { id: '1', name: 'Cliente 1', email: 'cliente@teste.com', orders: 5 }
];

const mockOrders = [
  { id: '1', order_number: 'ORD-123', restaurant_name: 'Loja 1', status: 'confirmed', total: 89.9, payment_method: 'pix' },
  { id: '2', order_number: 'ORD-124', restaurant_name: 'Loja 1', status: 'delivered', total: 74.9, payment_method: 'voucher' }
];

const mockStoreReviews = [
  {
    id: 'review-1',
    order_id: 'order-1',
    order_number: 'ORD-100001',
    restaurant_id: '1',
    restaurant_name: 'Loja 1',
    customer_id: 'customer-1',
    customer_name: 'Cliente 1',
    customer_email: 'cliente@teste.com',
    rating: 5,
    comment: 'Entrega rapida e comida muito boa.',
    status: 'published',
    moderation_reason: null,
    created_at: new Date().toISOString(),
    moderated_at: null,
    moderated_by: null,
    moderated_by_email: null,
    can_hide: true,
    can_publish: false,
  },
  {
    id: 'review-2',
    order_id: 'order-2',
    order_number: 'ORD-100002',
    restaurant_id: '1',
    restaurant_name: 'Loja 1',
    customer_id: 'customer-2',
    customer_name: 'Cliente 2',
    customer_email: 'cliente2@teste.com',
    rating: 2,
    comment: 'Demorou e chegou frio.',
    status: 'hidden',
    moderation_reason: 'Conteudo ofensivo identificado.',
    created_at: new Date().toISOString(),
    moderated_at: new Date().toISOString(),
    moderated_by: 'admin-1',
    moderated_by_email: 'admin@teste.com',
    can_hide: false,
    can_publish: true,
  },
];

const mockFees = {
  global_percent: 10,
  rules: []
};

const mockDeliveryFees = {
  global_fee_reais: 0,
  base_radius_km: 3,
  extra_km_fee_reais: 0,
  rules: []
};

const mockDeliveryRadius = {
  global_order_radius_km: 7,
  courier_accept_radius_m: 10000,
  courier_assign_radius_m: 10000,
  rules: []
};

const mockEnviosViagensSettings = {
  enabled: true,
  base_fee_reais: 8,
  base_radius_km: 3,
  extra_km_fee_reais: 1,
  store_id: null,
  payment_methods: ['pix', 'credit_card', 'debit_card'],
};

const mockFinance = {
  account: null
};

const mockContent = {
  theme_name: 'correfood',
  palette: {}
};

const mockBanners = {
  items: [
    { id: 'banner-1', image_url: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=1200', title: 'Desconto de Inauguração', subtitle: 'Use o cupom CORREFOOD e ganhe 20% OFF', button_text: 'Aproveitar', badge: '20% OFF' }
  ],
  settings: { interval_ms: 5000, transition: 'fade' },
  cta: { enabled: true, text: 'Ver ofertas', link: '/promocoes' }
};

const mockAdminTheme = {
  palette: {
    background: '#0B0B0F',
    surface: '#12121A',
    border: '#1F1F2A',
    text: '#FFFFFF',
    muted: '#B8B8C5',
    accent: '#FF6A00'
  }
};

const mockAudit = [
  { id: '1', actor_email: 'admin@teste.com', action: 'login', entity_type: 'system', created_at: new Date().toISOString() }
];

const mockSupportContact = {
  whatsapp_numbers: ['44999793369'],
  default_whatsapp: '44999793369'
};

const mockPromotionConfig = {
  enabled: false,
  code: 'CORREFOOD',
  discount_percent: 0,
  apply_on_promotional_products: true
};

const mockCommerceSettings = {
  admin_fee_percent: 10,
  admin_coupon: mockPromotionConfig
};

export const adminDashboard = {
  getSummary(period = 'day') {
    return requestWithFallback(
      () => apiClient.get(`/api/v2/admin/summary?period=${period}`),
      { ...mockSummary, period }
    );
  }
};

export const stores = {
  list({ status, q, voucherStatus } = {}) {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (q) params.set('q', q);
    if (voucherStatus) params.set('voucher_status', voucherStatus);
    const query = params.toString();
    return requestStrict(() => apiClient.get(`/api/v2/admin/stores${query ? `?${query}` : ''}`));
  },
  getById(id) {
    return requestStrict(() => apiClient.get(`/api/v2/admin/stores/${id}`));
  },
  approve(id, reason) {
    return apiClient.post(`/api/v2/admin/stores/${id}/approve`, { reason });
  },
  reject(id, reason) {
    return apiClient.post(`/api/v2/admin/stores/${id}/reject`, { reason });
  },
  updateStatus(id, status, reason) {
    return apiClient.post(`/api/v2/admin/stores/${id}/status?status=${status}`, { reason });
  },
  getOrders(id, { status, payment, limit } = {}) {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (payment) params.set('payment', payment);
    if (limit) params.set('limit', String(limit));
    const query = params.toString();
    return requestStrict(() => apiClient.get(`/api/v2/admin/stores/${id}/orders${query ? `?${query}` : ''}`));
  },
  getProducts(id, { q, available } = {}) {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (available !== undefined && available !== null && available !== '') params.set('available', String(available));
    const query = params.toString();
    return requestStrict(() => apiClient.get(`/api/v2/admin/stores/${id}/products${query ? `?${query}` : ''}`));
  },
  getFinance(id) {
    return requestStrict(() => apiClient.get(`/api/v2/admin/stores/${id}/finance`));
  },
  getAudit(id, { limit } = {}) {
    const query = limit ? `?limit=${limit}` : '';
    return requestStrict(() => apiClient.get(`/api/v2/admin/stores/${id}/audit${query}`));
  },
  getSettings(id) {
    return requestStrict(() => apiClient.get(`/api/v2/admin/stores/${id}/settings`));
  },
  getVoucherCommissions(id, { status, limit } = {}) {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (limit) params.set('limit', String(limit));
    const query = params.toString();
    return requestStrict(() => apiClient.get(`/api/v2/admin/stores/${id}/voucher-commissions${query ? `?${query}` : ''}`));
  },
  getVoucherSettings(id) {
    return requestStrict(() => apiClient.get(`/api/v2/admin/stores/${id}/voucher`));
  },
  reviewVoucher(id, payload) {
    return requestStrict(() => apiClient.post(`/api/v2/admin/stores/${id}/voucher/review`, payload));
  },
  getTaxProfile(id) {
    return requestStrict(() => apiClient.get(`/api/v2/admin/stores/${id}/tax-profile`));
  },
  reviewTaxProfile(id, payload) {
    return requestStrict(() => apiClient.patch(`/api/v2/admin/stores/${id}/tax-profile/review`, payload));
  },
  updateSettings(id, payload) {
    return apiClient.post(`/api/v2/admin/stores/${id}/settings`, payload);
  },
  updateCashDebt(id, orderId, payload) {
    return apiClient.post(`/api/v2/admin/stores/${id}/cash-debts/${orderId}`, payload);
  }
};

export const couriers = {
  list({ status } = {}) {
    const query = status ? `?status=${status}` : '';
    return requestWithFallback(
      () => apiClient.get(`/api/v2/admin/couriers${query}`),
      mockCouriers
    );
  },
  getRealtimeLocations() {
    return requestWithFallback(
      () => apiClient.get('/api/v2/admin/couriers/locations/realtime'),
      mockCouriers.filter((item) => item.is_online)
    );
  },
  approve(id, reason) {
    return apiClient.post(`/api/v2/admin/couriers/${id}/approve`, { reason });
  },
  reject(id, reason) {
    return apiClient.post(`/api/v2/admin/couriers/${id}/reject`, { reason });
  },
  block(id, reason) {
    return apiClient.post(`/api/v2/admin/couriers/${id}/block`, { reason });
  },
  listVerifications({ status } = {}) {
    const query = status ? `?status=${status}` : '';
    return requestWithFallback(
      () => apiClient.get(`/api/v2/admin/couriers/verifications${query}`),
      mockCourierVerifications
    );
  },
  getVerification(id) {
    return requestWithFallback(
      () => apiClient.get(`/api/v2/admin/couriers/${id}/verification`),
      mockCourierVerifications.find((item) => item.id === id) || mockCourierVerifications[0]
    );
  },
  reviewVerification(id, payload) {
    return apiClient.post(`/api/v2/admin/couriers/${id}/verification/review`, payload);
  },
  getFinanceSettings() {
    return apiClient
      .get('/api/v2/admin/couriers/finance/settings')
      .then((response) => response.data);
  },
  updateFinanceSettings(payload) {
    return apiClient
      .post('/api/v2/admin/couriers/finance/settings', payload)
      .then((response) => response.data);
  },
  settleFinance(id, payload = { scope: 'due' }) {
    return apiClient.post(`/api/v2/admin/couriers/${id}/finance/settle`, payload);
  },
  getOrders(id, { statusGroup } = {}) {
    const params = new URLSearchParams();
    if (statusGroup) params.set('status_group', statusGroup);
    const query = params.toString();
    return apiClient
      .get(`/api/v2/admin/couriers/${id}/orders${query ? `?${query}` : ''}`)
      .then((response) => response.data);
  }
};

export const customers = {
  list({ q } = {}) {
    const query = q ? `?q=${q}` : '';
    return requestWithFallback(
      () => apiClient.get(`/api/v2/admin/customers${query}`),
      mockCustomers
    );
  }
};

export const orders = {
  list({ status, payment, paymentStatus, storeId } = {}) {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (payment) params.set('payment', payment);
    if (paymentStatus) params.set('payment_status', paymentStatus);
    if (storeId) params.set('store_id', storeId);
    const query = params.toString();
    return requestStrict(() => apiClient.get(`/api/v2/admin/orders${query ? `?${query}` : ''}`));
  }
};

export const storeReviews = {
  list({ status, q, storeId, limit, page, pageSize, sortBy, sortDir } = {}) {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (q) params.set('q', q);
    if (storeId) params.set('store_id', storeId);
    if (limit) params.set('limit', String(limit));
    if (page) params.set('page', String(page));
    if (pageSize) params.set('page_size', String(pageSize));
    if (sortBy) params.set('sort_by', sortBy);
    if (sortDir) params.set('sort_dir', sortDir);
    const query = params.toString();
    const fallbackData = (page || pageSize)
      ? {
          items: mockStoreReviews,
          total: mockStoreReviews.length,
          page: Number(page || 1),
          page_size: Number(pageSize || 30),
          pages: 1,
          sort_by: String(sortBy || 'created_at'),
          sort_dir: String(sortDir || 'desc'),
        }
      : mockStoreReviews;
    return requestWithFallback(
      () => apiClient.get(`/api/v2/admin/reviews${query ? `?${query}` : ''}`),
      fallbackData,
    );
  },
  moderate(id, payload) {
    return requestStrict(() => apiClient.patch(`/api/v2/admin/reviews/${id}/moderate`, payload));
  },
  moderateBatch(payload) {
    return requestStrict(() => apiClient.patch('/api/v2/admin/reviews/moderate/batch', payload));
  },
};

export const fees = {
  get() {
    return requestWithFallback(
      () => apiClient.get('/api/v2/admin/fees'),
      mockFees
    );
  },
  update(payload) {
    return apiClient.post('/api/v2/admin/fees', payload);
  }
};

export const deliveryFees = {
  get() {
    return requestWithFallback(
      () => apiClient.get('/api/v2/admin/delivery-fees'),
      mockDeliveryFees
    );
  },
  update(payload) {
    return apiClient.post('/api/v2/admin/delivery-fees', payload);
  }
};

export const deliveryRadius = {
  get() {
    return requestWithFallback(
      () => apiClient.get('/api/v2/admin/delivery-radius'),
      mockDeliveryRadius
    );
  },
  update(payload) {
    return apiClient.post('/api/v2/admin/delivery-radius', payload);
  }
};

export const deliveryServices = {
  getEnviosViagens() {
    return requestWithFallback(
      () => apiClient.get('/api/v2/admin/delivery-services/envios-viagens'),
      mockEnviosViagensSettings
    );
  },
  updateEnviosViagens(payload) {
    return apiClient.post('/api/v2/admin/delivery-services/envios-viagens', payload);
  },
};

export const finance = {
  getAccount() {
    return requestWithFallback(
      () => apiClient.get('/api/v2/admin/finance/payout-account'),
      mockFinance
    );
  },
  updateAccount(payload) {
    return apiClient.post('/api/v2/admin/finance/payout-account', payload);
  },
  getStorePayouts({ q, status, limit } = {}) {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (status) params.set('status', status);
    if (limit) params.set('limit', String(limit));
    const query = params.toString();
    return requestWithFallback(
      () => apiClient.get(`/api/v2/admin/finance/stores/repasses${query ? `?${query}` : ''}`),
      mockStorePayouts
    );
  },
  settleStorePayout(storeId, payload = { scope: 'pending' }) {
    return apiClient
      .post(`/api/v2/admin/finance/stores/${storeId}/repasses/settle`, payload)
      .then((response) => response.data);
  }
};

export const content = {
  getTheme() {
    return requestWithFallback(
      () => apiClient.get('/api/v2/admin/content/theme'),
      mockContent
    );
  },
  updateTheme(payload) {
    return apiClient.post('/api/v2/admin/content/theme', payload);
  },
  getBanner() {
    return requestWithFallback(
      () => apiClient.get('/api/v2/admin/content/banner'),
      { image_url: '', title: '', subtitle: '', button_text: '' }
    );
  },
  updateBanner(payload) {
    return apiClient.post('/api/v2/admin/content/banner', payload);
  },
  getBanners() {
    return requestWithFallback(
      () => apiClient.get('/api/v2/admin/content/banners'),
      mockBanners
    );
  },
  updateBanners(payload) {
    return apiClient.post('/api/v2/admin/content/banners', payload);
  },
  uploadBanner(file) {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/api/v2/admin/content/banners/upload', formData);
  },
  getAdminTheme() {
    return requestWithFallback(
      () => apiClient.get('/api/v2/admin/content/admin-theme'),
      mockAdminTheme
    );
  },
  updateAdminTheme(payload) {
    return apiClient.post('/api/v2/admin/content/admin-theme', payload);
  }
};

function parseAuditDateFilter(value, { endOfDay = false } = {}) {
  const raw = String(value || '').trim();
  if (!raw) return null;

  const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/;
  const normalizedRaw = dateOnlyPattern.test(raw)
    ? `${raw}T${endOfDay ? '23:59:59.999' : '00:00:00.000'}`
    : raw;
  const parsed = new Date(normalizedRaw);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

function getFilteredMockAuditRows({
  limit,
  entityType,
  actionPrefix,
  actorEmail,
  q,
  createdFrom,
  createdTo,
} = {}) {
  const normalizedEntityType = String(entityType || '').trim().toLowerCase();
  const normalizedActionPrefix = String(actionPrefix || '').trim().toLowerCase();
  const normalizedActorEmail = String(actorEmail || '').trim().toLowerCase();
  const normalizedSearch = String(q || '').trim().toLowerCase();
  const createdFromDate = parseAuditDateFilter(createdFrom, { endOfDay: false });
  const createdToDate = parseAuditDateFilter(createdTo, { endOfDay: true });

  const filtered = (Array.isArray(mockAudit) ? mockAudit : []).filter((item) => {
    const itemEntityType = String(item?.entity_type || '').trim().toLowerCase();
    const itemAction = String(item?.action || '').trim().toLowerCase();
    const itemActorEmail = String(item?.actor_email || '').trim().toLowerCase();
    const itemEntityId = String(item?.entity_id || '').trim().toLowerCase();
    const itemCreatedAt = parseAuditDateFilter(item?.created_at);

    if (normalizedEntityType && itemEntityType !== normalizedEntityType) return false;
    if (normalizedActionPrefix && !itemAction.startsWith(normalizedActionPrefix)) return false;
    if (normalizedActorEmail && !itemActorEmail.includes(normalizedActorEmail)) return false;
    if (normalizedSearch) {
      const haystack = `${itemAction} ${itemEntityType} ${itemActorEmail} ${itemEntityId}`;
      if (!haystack.includes(normalizedSearch)) return false;
    }
    if (createdFromDate && (!itemCreatedAt || itemCreatedAt < createdFromDate)) return false;
    if (createdToDate && (!itemCreatedAt || itemCreatedAt > createdToDate)) return false;
    return true;
  });

  filtered.sort((left, right) => {
    const leftTime = parseAuditDateFilter(left?.created_at)?.getTime() || 0;
    const rightTime = parseAuditDateFilter(right?.created_at)?.getTime() || 0;
    return rightTime - leftTime;
  });

  if (limit) {
    return filtered.slice(0, Math.max(1, Number(limit) || 1));
  }
  return filtered;
}

function toAuditCsvCell(value) {
  const rawValue = (() => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  })();
  return `"${rawValue.replace(/"/g, '""').replace(/\r?\n/g, ' ')}"`;
}

function buildAuditCsvBlob(rows) {
  const headers = [
    'created_at',
    'actor_email',
    'action',
    'entity_type',
    'entity_id',
    'reason',
    'processed_count',
    'updated_count',
    'target_status',
    'details_json',
  ];

  const lines = [headers.map((header) => toAuditCsvCell(header)).join(';')];
  (rows || []).forEach((row) => {
    const details = row?.details && typeof row.details === 'object' ? row.details : {};
    lines.push([
      row?.created_at || '',
      row?.actor_email || '',
      row?.action || '',
      row?.entity_type || '',
      row?.entity_id || '',
      details.reason || '',
      Number(details.processed_count || 0),
      Number(details.updated_count || 0),
      details.target_status || '',
      details,
    ].map((value) => toAuditCsvCell(value)).join(';'));
  });

  return new Blob([`\uFEFF${lines.join('\n')}`], { type: 'text/csv;charset=utf-8;' });
}

export const audit = {
  list({ limit, page, pageSize, entityType, actionPrefix, actorEmail, q, createdFrom, createdTo } = {}) {
    const params = new URLSearchParams();
    if (limit) params.set('limit', String(limit));
    if (page) params.set('page', String(page));
    if (pageSize) params.set('page_size', String(pageSize));
    if (entityType) params.set('entity_type', String(entityType));
    if (actionPrefix) params.set('action_prefix', String(actionPrefix));
    if (actorEmail) params.set('actor_email', String(actorEmail));
    if (q) params.set('q', String(q));
    if (createdFrom) params.set('created_from', String(createdFrom));
    if (createdTo) params.set('created_to', String(createdTo));
    const query = params.toString();

    const filteredMockAudit = getFilteredMockAuditRows({
      limit,
      entityType,
      actionPrefix,
      actorEmail,
      q,
      createdFrom,
      createdTo,
    });
    const effectivePage = Math.max(1, Number(page || 1) || 1);
    const effectivePageSize = Math.max(1, Number(pageSize || 30) || 30);
    const pagedMockAudit = filteredMockAudit.slice(
      (effectivePage - 1) * effectivePageSize,
      (effectivePage - 1) * effectivePageSize + effectivePageSize,
    );
    const fallbackData = (page || pageSize)
      ? {
          items: pagedMockAudit,
          total: filteredMockAudit.length,
          page: effectivePage,
          page_size: effectivePageSize,
          pages: Math.ceil(filteredMockAudit.length / effectivePageSize),
        }
      : filteredMockAudit;
    return requestWithFallback(
      () => apiClient.get(`/api/v2/admin/audit/logs${query ? `?${query}` : ''}`),
      fallbackData
    );
  },

  async exportCsv({ limit, entityType, actionPrefix, actorEmail, q, createdFrom, createdTo } = {}) {
    const params = new URLSearchParams();
    if (limit) params.set('limit', String(limit));
    if (entityType) params.set('entity_type', String(entityType));
    if (actionPrefix) params.set('action_prefix', String(actionPrefix));
    if (actorEmail) params.set('actor_email', String(actorEmail));
    if (q) params.set('q', String(q));
    if (createdFrom) params.set('created_from', String(createdFrom));
    if (createdTo) params.set('created_to', String(createdTo));
    const query = params.toString();

    try {
      const response = await apiClient.get(`/api/v2/admin/audit/logs/export.csv${query ? `?${query}` : ''}`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      if (!(mockFallbackEnabled && shouldFallback(error))) {
        throw error;
      }
      const fallbackRows = getFilteredMockAuditRows({
        limit: limit || 10000,
        entityType,
        actionPrefix,
        actorEmail,
        q,
        createdFrom,
        createdTo,
      });
      return buildAuditCsvBlob(fallbackRows);
    }
  },
};

export const settings = {
  getSupportContact() {
    return requestWithFallback(
      () => apiClient.get('/api/v2/admin/config/support-contact'),
      mockSupportContact
    );
  },
  updateSupportContact(payload) {
    return apiClient.post('/api/v2/admin/config/support-contact', payload);
  },
  getPublicSupportContact() {
    return requestWithFallback(
      () => apiClient.get('/api/v2/admin/public/support-contact'),
      mockSupportContact
    );
  },
  getPromotionsConfig() {
    return requestWithFallback(
      () => apiClient.get('/api/v2/admin/config/promotions'),
      mockPromotionConfig
    );
  },
  updatePromotionsConfig(payload) {
    return apiClient.post('/api/v2/admin/config/promotions', payload);
  },
  getPublicCommerceSettings() {
    return requestWithFallback(
      () => apiClient.get('/api/v2/admin/public/commerce-settings'),
      mockCommerceSettings
    );
  }
};

